/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

class CountrySwitcherModal extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  #previousScrollY = 0;
  #isClosing = false;

  connectedCallback() {
    this.#dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    this.#form = /** @type {HTMLFormElement | null} */ (this.querySelector('.country-switcher-modal__form'));

    if (!this.#dialog) return;

    this.#appendSearchParamsToAnchors();
    this.#bindTriggers();
    this.#dialog.addEventListener('click', this.#handleDialogClick);
    this.#dialog.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#handleCancel);
  }

  #appendSearchParamsToAnchors() {
    const searchUrl = location.search;
    if (!searchUrl) return;

    for (const anchor of /** @type {NodeListOf<HTMLAnchorElement>} */ (this.querySelectorAll('[data-iso]'))) {
      anchor.href = anchor.href + searchUrl;
    }
  }

  #bindTriggers() {
    for (const trigger of /** @type {NodeListOf<HTMLElement>} */ (
      document.querySelectorAll('[data-country-switcher-modal-trigger]')
    )) {
      if (trigger.dataset.countrySwitcherBound) continue;
      trigger.dataset.countrySwitcherBound = 'true';
      trigger.addEventListener('click', this.#handleTriggerClick);
    }
  }

  /** @param {MouseEvent} e */
  #handleTriggerClick = (e) => {
    e.preventDefault();
    this.open();
  };

  /** @param {MouseEvent} e */
  #handleDialogClick = (e) => {
    const anchor = /** @type {HTMLElement} */ (e.target)?.closest('[data-iso]');

    if (anchor) {
      const anchorEl = /** @type {HTMLAnchorElement} */ (anchor);
      e.preventDefault();
      this.#handleSwitch({
        iso: anchorEl.dataset.iso ?? '',
        url: anchorEl.href,
        useMarkets: anchorEl.dataset.useMarkets === 'true',
      });
      return;
    }

    if (/** @type {HTMLElement} */ (e.target)?.closest('[data-close-dialog]')) {
      this.close();
      return;
    }

    if (e.target === this.#dialog) {
      this.close();
    }
  };

  /** @param {Event} e */
  #handleCancel = (e) => {
    e.preventDefault();
    this.close();
  };

  open() {
    if (!this.#dialog || this.#dialog.open || this.#isClosing) return;

    this.#previousScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
    this.#dialog.showModal();
  }

  async close() {
    if (!this.#dialog?.open || this.#isClosing) return;

    this.#isClosing = true;
    this.#dialog.classList.add('dialog-closing');

    await new Promise((resolve) => {
      this.#dialog?.addEventListener('animationend', resolve, { once: true });
    });

    this.#dialog.classList.remove('dialog-closing');
    this.#isClosing = false;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#dialog.close();
  }

  /** @param {{ iso: string, url: string, useMarkets: boolean }} params */
  #handleSwitch({ iso, url, useMarkets = false }) {
    if (!iso || !url) return;

    this.#setIso(iso);

    if (!useMarkets) {
      window.location.href = url;
      return;
    }

    const [lang, country] = /** @type {[string, string]} */ (iso.split('-'));
    let language = lang;

    if (language.endsWith('-dev')) {
      language = language.replace('-dev', '');
    }

    if (language === 'en' && window.location.href.includes('alpinestars-stage.myshopify.com')) {
      language = 'en-dev';
    }

    /** @type {HTMLInputElement} */ (this.#form?.querySelector('input[name="language_code"]')).value = language;
    /** @type {HTMLInputElement} */ (this.#form?.querySelector('input[name="country_code"]')).value = country;
    /** @type {HTMLInputElement} */ (this.#form?.querySelector('input[type="submit"]')).click();
  }

  /** @param {string} iso */
  #setIso(iso) {
    if (!iso) return;

    const storage = { iso, timestamp: Date.now() };
    localStorage.setItem('preferred-locale', JSON.stringify(storage));

    return storage;
  }
}

customElements.define('country-switcher-modal', CountrySwitcherModal);
